import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { Item } from "../../../../../../../models/item";
import { OnboardingStage } from "../../../../../../../models/onboarding.stage";
import { Filters, Searches, Sorts, Sort } from "../../../../../../../models/shipeezi.table";
import ItemService from "../../../../../../../services/items.service";
import OnboardingService from "../../../../../../../services/onBoarding.service";

const useActions = (refetchIsoCertificate: () => void, onboardingStage?: OnboardingStage) => {
  const { currentUser } = useAppState();
  const queryClient = useQueryClient();

  const [items, setItems] = useState<Item[]>([]);
  const [hasErrors, setHasErrors] = useState<boolean | undefined>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();

  const { mutate: removeIsoCertificate } = useMutation(
    ["onboarding.inventory.changed"],
    () =>
      OnboardingService.update(onboardingStage?.id || 0, {
        ...onboardingStage,
        inventoryWasUploaded: true,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["onboarding.get"]);
        refetchIsoCertificate();
      },
    }
  );

  const { refetch, isFetching } = useQuery(
    ["items.list", page, pageSize, filters, searches, sorting],
    () =>
      ItemService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        sortBy: sorting
          ? JSON.stringify([...sorting, ["name", "asc"]])
          : JSON.stringify([["name", "asc"]]),
        include: JSON.stringify([
          { association: "rooms" },
          { association: "packagings" },
          { association: "packingMaterials" },
        ]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setItems(response.data);
      },
    }
  );

  const { refetch: refetchItemsTotal } = useQuery(
    ["items.count", filters, searches],
    () =>
      ItemService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
        else setTotal(0);
      },
    }
  );

  const { mutate: updateItem, isLoading: isUpdating } = useMutation(
    ["item.update"],
    ({ id, item }: { id: number; item: any }) => ItemService.update(id, item),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        refetchItemsTotal();
        if (onboardingStage && !onboardingStage.inventoryWasUploaded) removeIsoCertificate();
      },
      onError: (error: any) => {
        NotificationToast.error(error.response.data.message);
        setHasErrors(true);
      },
    }
  );

  const { mutate: updateItemList, isLoading: isUpdatingList } = useMutation(
    ["item.update.list"],
    (items: any[]) => ItemService.updateList(items),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        refetchItemsTotal();
      },
      onError: (error: any) => {
        NotificationToast.error(error.response.data.message);
        setHasErrors(true);
      },
    }
  );

  const { mutate: updateItemStatus, isLoading: isUpdatingItemStatus } = useMutation(
    ["item.update.status"],
    ({ id, item }: { id: number; item: any }) => ItemService.update(id, item),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        refetchItemsTotal();
      },
      onError: (error: any) => {
        NotificationToast.error(error.response.data.message);
        setHasErrors(true);
      },
    }
  );

  const { mutate: createItem, isLoading: isCreating } = useMutation(
    ["item.create"],
    (item: any) => ItemService.create(item),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        refetchItemsTotal();
        if (onboardingStage && !onboardingStage.inventoryWasUploaded) removeIsoCertificate();
      },
      onError: (error: any) => {
        NotificationToast.error(error.response.data.message);
        setHasErrors(true);
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    items,
    total,
    page,
    pageSize,
    hasErrors,
    isLoading: isFetching || isCreating || isUpdating || isUpdatingItemStatus || isUpdatingList,
    setPageSize,
    setHasErrors,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createItem,
    updateItem,
    updateItemStatus,
    updateItemList,
  };
};

export default useActions;
